import React, { useState, useEffect } from 'react'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import axios from 'axios'
import { BASE_URL } from '../../utils/api'
import { ErrorNotification, SuccessNotification } from '../../utils'
import Spinner from '../Spinner'
import { useLocationCascade } from '../../hooks/useLocations'

const AddProcessorModal = ({ showAddModal, setShowAddModal, mutate }) => {
  const [region, setRegion] = useState("")
  const [district, setDistrict] = useState("")
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(false)
  const [processors, setProcessors] = useState([])
  const [addLoadingId, setAddLoadingId] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [pendingProcessor, setPendingProcessor] = useState(null)
  const [community, setCommunity] = useState("")
  const [notes, setNotes] = useState("")

  const communityDistrict =
    pendingProcessor && !pendingProcessor.community
      ? pendingProcessor.district
      : district
  const { regionNames, districts, communities, isLoading: locationsLoading } = useLocationCascade(
    region,
    communityDistrict
  )

  // Reset district when region changes
  useEffect(() => {
    setDistrict("")
  }, [region])

  // Fetch all processors on open, and whenever filters/search change
  useEffect(() => {
    if (showAddModal) {
      fetchProcessors()
    }
    // eslint-disable-next-line
  }, [showAddModal, region, district, search])

  const handleReset = () => {
    setRegion("")
    setDistrict("")
    setSearch("")
  }

  const fetchProcessors = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem("token")
      let url = `${BASE_URL}provider-processor/connections/available-users/?`
      if (region) url += `region=${encodeURIComponent(region)}&`
      if (district) url += `district=${encodeURIComponent(district)}&`
      if (search) url += `search=${encodeURIComponent(search)}&`
      url = url.replace(/&$/, "")
      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setProcessors(data || [])
    } catch (e) {
      setProcessors([])
      ErrorNotification(e?.message || "Failed to fetch processors.")
    } finally {
      setLoading(false)
    }
  }

  const handleAddClick = (proc) => {
    setPendingProcessor(proc)
    setCommunity(proc.community || "")
    setNotes("")
    setShowDetailsModal(true)
  }

  const handleConfirmAdd = async () => {
    setAddLoadingId(pendingProcessor.id)
    try {
      const token = localStorage.getItem("token")
      await axios.post(
        `${BASE_URL}provider-processor/connections/`,
        {
          processor: pendingProcessor.id,
          region: pendingProcessor.region,
          district: pendingProcessor.district,
          community: pendingProcessor.community || community,
          notes: notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      mutate()
      SuccessNotification("Processor added successfully!")
      setShowAddModal(false)
      setShowDetailsModal(false)
    } catch (e) {
      ErrorNotification(e?.response?.data?.detail || e?.message || "Failed to add processor.")
    } finally {
      setAddLoadingId(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full md:max-w-4xl relative max-h-[90vh] flex flex-col overflow-hidden">
        {/* Sticky Header with Close Button */}
        <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 sticky top-0 bg-white dark:bg-dark z-10'>
          <h2 className='text-xl font-bold dark:text-white'>Add Processor</h2>
          <button
            onClick={() => setShowAddModal(false)}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-6 w-6" />
          </button>
        </div>
        {/* Sticky Filter/Search Bar */}
        <div className="flex flex-col md:flex-row gap-4 px-4 py-3 sticky top-[64px] bg-white dark:bg-dark z-10 border-b border-gray-100 dark:border-border-dark">
          <div className="flex flex-col w-full md:w-1/3">
            <label className="text-sm font-medium mb-1 dark:text-white">Region</label>
            <select
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3"
              value={region}
              onChange={e => setRegion(e.target.value)}
            >
              <option value="">All Regions</option>
              {regionNames.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="flex flex-col w-full md:w-1/3">
            <label className="text-sm font-medium mb-1 dark:text-white">District</label>
            <select
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3"
              value={district}
              onChange={e => setDistrict(e.target.value)}
              disabled={!region}
            >
              <option value="">All Districts</option>
              {districts.map(d => <option key={d.name} value={d.name}>{d.name}</option>)}
            </select>
          </div>
          <div className="flex flex-col w-full md:w-1/3">
            <label className="text-sm font-medium mb-1 dark:text-white">Search by Name</label>
            <div className="flex flex-row gap-2 items-center">
              <input
                className="w-full bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3"
                type="text"
                placeholder="Search processor name..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {(region || district || search) && (
                <button
                  onClick={handleReset}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded text-xs font-medium"
                  type="button"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>
        {/* List of Processors */}
        <div className="flex-1 overflow-y-auto p-4 relative">
          {loading && (
            <div className="absolute inset-0 bg-white/60 dark:bg-dark/60 flex items-center justify-center z-20">
              <div className='py-5'>
                <Spinner />
              </div>
            </div>
          )}
          {processors.length === 0 && !loading ? (
            <div className="text-center text-gray-500 mt-16">No processors found for the selected filters.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {processors.map(proc => (
                <div key={proc.id} className="bg-gray-50 dark:bg-darkGray rounded-lg shadow p-4 flex flex-col gap-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white dark:border-border-darkGray relative">
                  <div className="flex flex-row items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-yellowcolor/20 flex items-center justify-center text-xl font-bold text-yellowcolor">
                      {proc.firstname?.charAt(0)}{proc.lastname?.charAt(0)}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-semibold text-black dark:text-white text-base">{proc.firstname} {proc.lastname}</span>
                      <span className="text-xs text-gray-500 lowercase">{proc.email}</span>
                      <span className="text-xs text-gray-500">{proc.phone}</span>
                    </div>
                  </div>
                  <div className="flex flex-row gap-2 mt-2">
                    <span className="bg-yellowcolor/20 text-yellowcolor px-2 py-0.5 rounded text-xs">{proc.region}</span>
                    <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded text-xs">{proc.district}</span>
                  </div>
                  <button
                    className="mt-3 bg-yellowcolor text-black px-4 py-2 rounded-md text-sm font-medium self-end disabled:opacity-60"
                    disabled={addLoadingId === proc.id}
                    onClick={() => handleAddClick(proc)}
                  >
                    {addLoadingId === proc.id ? "Adding..." : "Add"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Details Modal for Community/Notes */}
        {showDetailsModal && pendingProcessor && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full max-w-md p-6 relative">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <IoIosCloseCircleOutline className="h-5 w-5" />
              </button>
              <h3 className="text-lg font-bold mb-4 dark:text-white">Add Processor Details</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 dark:text-white">Community (optional)</label>
                {pendingProcessor.community ? (
                  <input
                    className="w-full bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 bg-gray-100"
                    value={pendingProcessor.community}
                    disabled
                  />
                ) : (
                  <select
                      className="w-full bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3"
                    value={community}
                    onChange={e => setCommunity(e.target.value)}
                    disabled={locationsLoading}
                  >
                    <option value="">Select community</option>
                    {communities.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 dark:text-white">Notes (optional)</label>
                <textarea
                  className="w-full bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Specializes in crop processing, etc."
                />
              </div>
              <button
                className="w-full bg-yellowcolor text-black px-4 py-2 rounded-md text-sm font-medium mt-2 disabled:opacity-60"
                disabled={addLoadingId === pendingProcessor.id}
                onClick={handleConfirmAdd}
              >
                {addLoadingId === pendingProcessor.id ? "Adding..." : "Confirm Add"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AddProcessorModal
